import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Chat, { DayHeaderFormat, MessageTimestampFormat } from 'devextreme-react/chat';
import type {
  MessageDeletedEvent,
  MessageEnteredEvent,
  MessageTemplateData,
  MessageUpdatedEvent,
  TypingStartEvent
} from './devextremeChatEvents';
import LoadIndicator from 'devextreme-react/load-indicator';
import type { CSSProperties } from 'react';
import type { FileReviewMessage } from './types';
import {
  mapReviewMessagesToChatItems,
  truncateAnchorQuote,
  type FileReviewChatItem
} from './reviewChatItems';
import { MentionCandidatePicker } from './MentionCandidatePicker';
import { MentionComposePanel } from './MentionComposePanel';
import { DiscussionComposeToolbar } from './emoji/DiscussionComposeToolbar';
import { insertTextAtComposeCaret } from './emoji/insertAtComposeCaret';
import { applyMentionToken, getMentionPickerQuery } from './mentionPicker';
import { configureComposeAssist } from './configureComposeAssist';
import { resolveChatComposeElement } from './resolveChatComposeElement';
import { useChatComposeStackOffset } from './useChatComposeStackOffset';
import { useEmailMentionAssist, type MentionComposeElement } from './useEmailMentionAssist';
import { renderMentionText } from './renderMentionText';
import { openChatMessageActions } from './openChatMessageActions';
import './mentionEmail.css';
import './FileReviewChat.css';

export interface FileReviewChatProps {
  collapsed?: boolean;
  panelStyle?: CSSProperties;
  messages: FileReviewMessage[];
  currentUserId: string;
  currentUserAliases?: Iterable<string>;
  currentUserName: string;
  currentUserHasPhoto?: boolean | null;
  loading?: boolean;
  readOnly?: boolean;
  canAnnotate?: boolean;
  activeAnchorId?: string | null;
  jumpLabel?: string;
  title?: string;
  onMessageEntered: (text: string) => void | Promise<void>;
  onMessageUpdated?: (messageId: string, text: string) => void | Promise<void>;
  onMessageDeleted?: (messageId: string) => void | Promise<void>;
  onMessageClick?: (anchorId: string | null) => void;
  /** Called when compose text has an incomplete @mention. */
  onComposeValidationError?: (message: string) => void;
  /** Storage mount for scoped mention picker API. */
  mountId?: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
}

function asChatItem(message: MessageTemplateData['message']): FileReviewChatItem | undefined {
  if (!message || typeof message !== 'object') return undefined;
  return message as FileReviewChatItem;
}

/** DevExtreme Chat only auto-scrolls when the user was already at the bottom; keep latest in view on updates. */
function scrollChatMessageListToBottom(host: HTMLElement | null): void {
  if (!host) return;
  const container = host.querySelector('.dx-chat-messagelist .dx-scrollable-container');
  if (!(container instanceof HTMLElement)) return;
  container.scrollTop = container.scrollHeight;
}

export function FileReviewChat({
  collapsed = false,
  panelStyle,
  messages,
  currentUserId,
  currentUserAliases = [],
  currentUserName,
  currentUserHasPhoto,
  loading = false,
  readOnly = false,
  canAnnotate = true,
  activeAnchorId,
  jumpLabel = 'Jump to selection',
  title = 'Discussion',
  onMessageEntered,
  onMessageUpdated,
  onMessageDeleted,
  onMessageClick,
  onComposeValidationError,
  mountId,
  path
}: FileReviewChatProps) {
  const chatHostRef = useRef<HTMLDivElement>(null);
  const [composeInput, setComposeInput] = useState<MentionComposeElement | null>(null);
  const [composeText, setComposeText] = useState('');
  const aliasSet = useMemo(() => {
    const set = new Set<string>();
    for (const value of currentUserAliases) {
      if (value) set.add(value);
    }
    if (currentUserId && currentUserId !== 'unknown') set.add(currentUserId);
    return set;
  }, [currentUserAliases, currentUserId]);
  /** DevExtreme Chat requires user.id === author.id (strict) for own-message chrome. */
  const identityReady = currentUserId !== 'unknown';
  const composeAssistEnabled = canAnnotate && !readOnly && identityReady;
  const { hintVisible, hintMessage, validateBeforeSend } = useEmailMentionAssist(composeInput, {
    enabled: composeAssistEnabled,
    text: composeText
  });
  const pickerQuery = useMemo(
    () => (composeAssistEnabled && mountId ? getMentionPickerQuery(composeText) : null),
    [composeAssistEnabled, composeText, mountId]
  );

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]?.id : null;

  const scrollToLatestMessage = useCallback(() => {
    const run = () => scrollChatMessageListToBottom(chatHostRef.current);
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
      window.setTimeout(run, 60);
    });
  }, []);

  useLayoutEffect(() => {
    if (collapsed || messages.length === 0) return;
    scrollToLatestMessage();
  }, [collapsed, lastMessageId, messages.length, scrollToLatestMessage]);

  const items = useMemo(
    () => mapReviewMessagesToChatItems(messages, currentUserId, aliasSet, currentUserHasPhoto),
    [aliasSet, messages, currentUserId, currentUserHasPhoto]
  );

  const selfPhotoUrl =
    currentUserHasPhoto !== false ? '/api/auth/profile/photo' : undefined;

  const user = useMemo(
    () => ({
      id: currentUserId,
      name: currentUserName,
      ...(selfPhotoUrl ? { avatarUrl: selfPhotoUrl } : {})
    }),
    [currentUserId, currentUserName, selfPhotoUrl]
  );

  const editingEnabled = canAnnotate && !readOnly && identityReady;
  /**
   * DevExtreme only attaches the edit/delete context menu to alignment-end (own) bubbles.
   * Use boolean true once identity is ready — callback ownership checks were silently denying
   * the menu even when the bubble already rendered as the current user's.
   */
  const editingOptions = useMemo(
    () =>
      editingEnabled
        ? { allowUpdating: true as const, allowDeleting: true as const }
        : { allowUpdating: false as const, allowDeleting: false as const },
    [editingEnabled]
  );

  useChatComposeStackOffset(chatHostRef, !collapsed && editingEnabled && !loading);

  const bindComposeElement = useCallback((el: MentionComposeElement | null) => {
    if (el) {
      configureComposeAssist(el);
      setComposeInput((current) => (current === el ? current : el));
    }
  }, []);

  const onChatInitialized = useCallback(() => {
    bindComposeElement(resolveChatComposeElement(chatHostRef.current));
    const host = chatHostRef.current;
    const box = host?.querySelector('.dx-chat-messagebox');
    if (host && box instanceof HTMLElement) {
      const height = Math.ceil(box.getBoundingClientRect().height);
      if (height > 0) {
        host.style.setProperty('--file-review-chat-compose-stack-height', `${height}px`);
      }
    }
  }, [bindComposeElement]);

  useLayoutEffect(() => {
    if (collapsed || readOnly || !canAnnotate) return;
    if (composeInput) return;
    const found = resolveChatComposeElement(chatHostRef.current);
    if (found) bindComposeElement(found);
  }, [bindComposeElement, canAnnotate, collapsed, composeInput, items.length, loading, readOnly]);

  const onTypingStart = useCallback(
    (e: TypingStartEvent) => {
      const target = e.event?.target;
      if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
        bindComposeElement(target);
        setComposeText((current) => (current === target.value ? current : target.value));
      }
    },
    [bindComposeElement]
  );

  useLayoutEffect(() => {
    if (!composeInput || readOnly || !canAnnotate) return;
    const sync = () => setComposeText((current) => (current === composeInput.value ? current : composeInput.value));
    sync();
    composeInput.addEventListener('input', sync);
    return () => composeInput.removeEventListener('input', sync);
  }, [canAnnotate, composeInput, readOnly]);

  const onMentionPick = useCallback(
    (token: string) => {
      if (!composeInput || !pickerQuery) return;
      const next = applyMentionToken(composeText, pickerQuery.triggerIndex, token);
      composeInput.value = next;
      composeInput.dispatchEvent(new Event('input', { bubbles: true }));
      setComposeText(next);
    },
    [composeInput, composeText, pickerQuery]
  );

  const onEmojiPick = useCallback(
    (emoji: string) => {
      if (!composeInput) return;
      const next = insertTextAtComposeCaret(composeInput, emoji);
      setComposeText(next);
    },
    [composeInput]
  );

  const onEntered = useCallback(
    (e: MessageEnteredEvent) => {
      const text = e.message?.text?.trim();
      if (!text || readOnly || !canAnnotate) return;
      const validationError = validateBeforeSend(text);
      if (validationError) {
        onComposeValidationError?.(validationError);
        return;
      }
      void onMessageEntered(text);
    },
    [canAnnotate, onComposeValidationError, onMessageEntered, readOnly, validateBeforeSend]
  );

  const onUpdated = useCallback(
    (e: MessageUpdatedEvent) => {
      const id = e.message?.id;
      const text = e.text?.trim();
      if (id == null || !text || !onMessageUpdated) return;
      void onMessageUpdated(String(id), text);
    },
    [onMessageUpdated]
  );

  const onDeleted = useCallback(
    (e: MessageDeletedEvent) => {
      const id = e.message?.id;
      if (id == null || !onMessageDeleted) return;
      void onMessageDeleted(String(id));
    },
    [onMessageDeleted]
  );

  const messageRender = useCallback(
    (data: MessageTemplateData) => {
      const item = asChatItem(data.message);
      const text = item?.text ?? (data.message && 'text' in data.message ? String(data.message.text ?? '') : '');
      const anchorId = item?.anchorId ?? null;
      const quote = item?.anchorSelectedText?.trim();
      const isActive = Boolean(anchorId && anchorId === activeAnchorId);
      const showActions = Boolean(
        editingEnabled && item?.author?.id && item.author.id === currentUserId
      );

      return (
        <div
          className={`file-review-chat__bubble${isActive ? ' file-review-chat__bubble--active' : ''}${anchorId ? ' file-review-chat__bubble--anchored' : ''}`}
        >
          {showActions ? (
            <button
              type="button"
              className="file-review-chat__bubble-actions"
              aria-label="Edit or delete this message"
              title="Edit or delete"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openChatMessageActions(event.currentTarget);
              }}
            >
              ⋯
            </button>
          ) : null}
          <p className="file-review-chat__bubble-text">{renderMentionText(text)}</p>
          {quote ? (
            <blockquote className="file-review-chat__bubble-quote">
              &ldquo;{truncateAnchorQuote(quote)}&rdquo;
            </blockquote>
          ) : null}
          {anchorId && onMessageClick ? (
            <button
              type="button"
              className="file-review-chat__bubble-jump"
              onClick={(ev) => {
                ev.stopPropagation();
                onMessageClick(anchorId);
              }}
            >
              {jumpLabel}
            </button>
          ) : null}
        </div>
      );
    },
    [activeAnchorId, currentUserId, editingEnabled, jumpLabel, onMessageClick]
  );

  const asideStyle = collapsed ? undefined : panelStyle;

  return (
    <aside
      className={`file-review-chat${collapsed ? ' file-review-chat--collapsed' : ''}`}
      style={asideStyle}
      aria-label={title}
      aria-hidden={collapsed}
    >
      <header className="file-review-chat__header">
        <span className="file-review-chat__header-title">{title}</span>
        {editingEnabled ? (
          <span className="file-review-chat__header-hint">
            Type @ and pick someone from the list to notify them. Use ⋯ or right-click your message to edit or delete.
          </span>
        ) : null}
      </header>
      {loading && items.length === 0 ? (
        <div className="file-review-chat__loading" aria-busy="true">
          <LoadIndicator visible height={24} width={24} />
        </div>
      ) : (
        <div ref={chatHostRef} className="file-review-chat__widget-host">
        <Chat
          className={`file-review-chat__widget${selfPhotoUrl ? ' file-review-chat__widget--self-photo' : ''}`}
          style={
            selfPhotoUrl
              ? ({ '--file-review-chat-self-avatar': `url(${selfPhotoUrl})` } as CSSProperties)
              : undefined
          }
          items={items}
          user={user}
          editing={editingOptions}
          disabled={readOnly || !canAnnotate}
          reloadOnChange={false}
          showAvatar={true}
          showUserName={true}
          showDayHeaders={true}
          showMessageTimestamp={true}
          onMessageEntered={onEntered}
          onInitialized={onChatInitialized}
          onTypingStart={onTypingStart}
          onMessageUpdated={editingEnabled ? onUpdated : undefined}
          onMessageDeleted={editingEnabled ? onDeleted : undefined}
          messageRender={messageRender}
          height="100%"
        >
          <DayHeaderFormat type="shortDate" />
          <MessageTimestampFormat type="shortTime" />
        </Chat>
        {editingEnabled ? (
          <DiscussionComposeToolbar onEmojiSelect={onEmojiPick} disabled={!composeInput} />
        ) : null}
        <MentionComposePanel
          hintMessage={hintVisible ? hintMessage : null}
          showPicker={Boolean(mountId && pickerQuery)}
          picker={
            mountId && pickerQuery ? (
              <MentionCandidatePicker
                mountId={mountId}
                path={path}
                query={pickerQuery.query}
                open
                onOpenChange={() => undefined}
                onSelect={onMentionPick}
              />
            ) : null
          }
        />
        </div>
      )}
    </aside>
  );
}
